/**
 * Applications feature — Zod schemas.
 *
 * Applications are tenant-scoped OAuth/OIDC or SAML relying
 * parties. The protocol is a discriminant — the two protocol
 * variants carry different fields (client_id vs entity_id,
 * redirect_uris vs acs_url). We parse them with a loose schema
 * and narrow at the component.
 */

import { z } from "zod";
import { nameIDReportSchema } from "./name-identifiers.generated";
import { accessPolicySchema } from "./request-policy.generated";
export { nameIDReportSchema };
import { destinationScopeSchema } from "./destination-scope.generated";
export { destinationScopeSchema };

import {
  applicationWorkspaceCapabilitiesSchema,
  workspaceDecisionSchema,
} from "@/lib/application-capability-contract";

import { appUsageResponseSchema, publicApplicationEntrySchema } from "./reach.generated";
export { appUsageResponseSchema, publicApplicationEntrySchema };

export const protocolSchema = z.enum(["oidc", "saml", "smart_on_fhir", "private_access"]);
export type Protocol = z.infer<typeof protocolSchema>;

export const applicationTypeSchema = z.enum(["web", "spa", "native"]);
export type ApplicationType = z.infer<typeof applicationTypeSchema>;

export const clientTypeSchema = z.enum(["confidential", "public"]);
export type ClientType = z.infer<typeof clientTypeSchema>;

export const tokenEndpointAuthSchema = z.enum([
  "none",
  "client_secret_post",
  "client_secret_basic",
  "private_key_jwt",
]);
export type TokenEndpointAuth = z.infer<typeof tokenEndpointAuthSchema>;

// ── SAML NameID format ────────────────────────────────────────────────────
// The value the service provider receives as the Subject NameID, and the
// single most consequential SAML setting on the row: it decides both the
// FORMAT label on the assertion and the VALUE carried under it. Changing it
// on a live app re-keys accounts at the SP.
//
// This mirrors the applications.saml_name_id_format CHECK constraint and the
// dispatch in backend/internal/tenant/saml/nameid.go. All three must move
// together; the backend rejects anything outside this set.
/** The emailAddress format URN, named once so the format/source
 *  compatibility rule reads as a rule rather than a string compare. */
export const SAML_NAME_ID_FORMAT_EMAIL =
  "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress";

/** The CLOSED set of literal name-identifier sources, mirroring
 *  exprctx.IsValidNameIDSourceAttribute.
 *
 *  Every entry identifies ONE person. The rule is not "does it
 *  resolve" — department, job_title and status all resolve perfectly
 *  well, and to the same value for thousands of people, which merges
 *  them into a single account at the service provider. */
export function isValidNameIDSource(source: string): boolean {
  if (source === "email" || source === "employee_id") {
    return true;
  }
  // A tenant attribute; the server holds it to one kept unique and set once.
  if (/^x_[a-z0-9_]{1,61}$/.test(source)) {
    return true;
  }
  return false;
}

export const samlNameIDFormatSchema = z.enum([
  "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:transient",
  "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified",
]);
export type SamlNameIDFormat = z.infer<typeof samlNameIDFormatSchema>;

/** The format written into a new SAML row when the operator does not choose.
 *  Matches DefaultSAMLNameIDFormat in the backend and the database default.
 *  Kept explicit in the create payload rather than left to a server default,
 *  so the operator sees in the form what the row will say. */
export const DEFAULT_SAML_NAME_ID_FORMAT: SamlNameIDFormat =
  "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent";

/** Short operator-facing names. Sentence case per the UI conventions; the
 *  URI itself is the machine value and is rendered separately in mono. */
export const SAML_NAME_ID_FORMAT_LABELS: Record<SamlNameIDFormat, string> = {
  "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress": "Email address",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent": "Persistent",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:transient": "Transient",
  "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified": "Unspecified",
};

/** What the SP actually receives. Written as the consequence, not the
 *  standards definition — the operator's real question is "what value lands
 *  in the SP's user record", and getting it wrong creates duplicate accounts
 *  there. */
export const SAML_NAME_ID_FORMAT_DESCRIPTIONS: Record<SamlNameIDFormat, string> = {
  "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress":
    "Sends the user's email address. Sign-in fails for any user without one on file.",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent":
    "Sends a stable internal user ID that never changes. Safe default — works for every user.",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:transient":
    "Sends a fresh single-use value on every sign-in. Only for SPs that never store the identifier.",
  "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified":
    "Sends the user's email address and lets the SP decide how to read it. Common for on-premise SPs.",
};

/** Which formats carry the same value, and therefore which changes re-key
 *  accounts at the service provider. `persistent` and `transient` both derive
 *  from the internal user ID; `emailAddress` and `unspecified` both send the
 *  email address. Moving BETWEEN these groups changes what the SP stores. */
export function samlNameIDCarriesEmail(format: SamlNameIDFormat): boolean {
  return (
    format === "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress" ||
    format === "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified"
  );
}

/** True when moving from `from` to `to` changes the VALUE the SP receives,
 *  not just the format label — the case that re-keys existing accounts. */
export function samlNameIDChangeReKeys(from: SamlNameIDFormat, to: SamlNameIDFormat): boolean {
  if (from === to) return false;
  // transient is always a re-key in practice: the value is single-use, so an
  // SP that was matching on a stable value stops matching immediately.
  if (to === "urn:oasis:names:tc:SAML:2.0:nameid-format:transient") return true;
  if (from === "urn:oasis:names:tc:SAML:2.0:nameid-format:transient") return true;
  return samlNameIDCarriesEmail(from) !== samlNameIDCarriesEmail(to);
}

/** Picks the best supported format out of the list an SP advertises in its
 *  metadata (`<NameIDFormat>` elements, surfaced by discovery as
 *  `nameid_formats`). Returns null when the SP advertises nothing we support,
 *  in which case the form keeps the default and the operator decides.
 *
 *  We take the SP's OWN preference order: SAML metadata lists NameIDFormat
 *  elements most-preferred-first, so the first entry we support wins. This is
 *  the one signal in the whole flow that comes from the SP itself rather than
 *  from a guess, and second-guessing it with a ranking of our own would mean
 *  overriding the relying party about its own identifier.
 *
 *  Consequence worth knowing: an SP that lists `transient` first gets
 *  `transient` pre-selected. That is the SP's stated preference and it is
 *  honoured, but it is also the format an operator least often wants, so the
 *  picker shows what each choice actually sends and the operator can override
 *  before saving. (A previous version of this comment described a two-stage
 *  selection — SP order, then "our ranking" as a tie-break — that was never
 *  implemented. There is one stage.) */
export function pickAdvertisedNameIDFormat(advertised: readonly string[]): SamlNameIDFormat | null {
  for (const raw of advertised) {
    const parsed = samlNameIDFormatSchema.safeParse(raw.trim());
    if (parsed.success) return parsed.data;
  }
  return null;
}

/** Human labels for the token-endpoint authentication methods.
 *  Used by the create dialog's Advanced summary line and the
 *  detail page, so the two surfaces name the same thing the same
 *  way. */
export const TOKEN_ENDPOINT_AUTH_LABELS: Record<string, string> = {
  client_secret_post: "Client secret post",
  client_secret_basic: "Client secret basic",
  private_key_jwt: "Private key JWT",
  none: "No client authentication",
};

export const APPLICATION_TYPE_LABELS: Record<ApplicationType, string> = {
  web: "Web application",
  spa: "Single page app (SPA)",
  native: "Native / mobile",
};

export const APPLICATION_TYPE_DESCRIPTIONS: Record<ApplicationType, string> = {
  web: "Server-side app with a client secret (confidential client)",
  spa: "Browser app — no secret, PKCE required (public client)",
  native: "iOS, Android, or desktop app — no secret, PKCE required (public client)",
};

export function isPublicAppType(t: ApplicationType): boolean {
  return t === "spa" || t === "native";
}

// PROTOCOL_DISPLAY_LABEL is the canonical operator-facing rendering
// for each protocol — what shows up in page headers, metadata rows,
// audit narratives. The wire literal (smart_on_fhir) is shouty
// snake_case and renders as "SMART_ON_FHIR" through .toUpperCase();
// use this map instead so every surface agrees on
// "OIDC" / "SAML" / "SMART-on-FHIR".
//
// Per PM review 2026-05-27: the all-caps underscore form was the
// single most visible typography defect on the application detail
// page — a CIO would notice. Single source of truth lives here so
// "SMART-on-FHIR" doesn't drift into "SMART on FHIR" / "Smart on
// FHIR" / "SMART/FHIR" in other surfaces.
export const PROTOCOL_DISPLAY_LABEL: Record<Protocol, string> = {
  oidc: "OIDC",
  saml: "SAML",
  smart_on_fhir: "SMART-on-FHIR",
  private_access: "Private access",
};

// Conventional SMART-on-FHIR scope set for a clinical-viewer launch.
// Mirrors backend smart.DefaultScopes — keep both lists in sync; if
// you change one, change the other. Used by the create-tile form to
// pre-populate the scopes selector when the operator picks
// smart_on_fhir.
//
// `launch/patient` is the standalone-launch scope (asks the EHR to
// prompt for a patient picker). Bare `launch` is the EHR-launched
// scope and requires a `launch=<token>` parameter on the authorize
// request — PureID implements standalone launch only.
export const DEFAULT_SMART_SCOPES = [
  "launch/patient",
  "openid",
  "fhirUser",
  "patient/*.read",
  "offline_access",
] as const;

export const applicationStatusSchema = z.enum(["active", "disabled"]);
export type ApplicationStatus = z.infer<typeof applicationStatusSchema>;

// ── SAML NameID format ──────────────────────────────────────────────────
//
// The 4 standard SAML 2.0 NameID format URNs, mirroring the backend's
// validSAMLNameIDFormats (internal/tenant/application/application.go).
// Until the NameID resolution fix, this field was stored but cosmetic
// — the assertion always carried a hardcoded opaque user id NameID
// regardless of the configured format. It's now live: the picked
// format is what the SP actually receives, and (for the email format)
// the value automatically becomes the user's real email with zero
// extra configuration — see saml_name_id_source below for a fully
// custom value.
export const SAML_NAME_ID_FORMATS = [
  { value: "", label: "Default (opaque, stable identifier)" },
  { value: "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress", label: "Email address" },
  { value: "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent", label: "Persistent" },
  { value: "urn:oasis:names:tc:SAML:2.0:nameid-format:transient", label: "Transient" },
  { value: "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified", label: "Unspecified" },
] as const;

// ── List filter ───────────────────────────────────────────────────────────

export const applicationsListSearchSchema = z.object({
  q: z.string().optional(),
  protocol: protocolSchema.optional(),
  sortBy: z.enum(["name", "protocol", "created_at"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type ApplicationsListSearch = z.infer<typeof applicationsListSearchSchema>;

// ── Application record ────────────────────────────────────────────────────

export const applicationSchema = z.object({
  destination_scope: destinationScopeSchema.nullable().optional(),
  id: z.string(),
  name: z.string(),
  protocol: protocolSchema,
  application_type: applicationTypeSchema.optional(),
  client_type: clientTypeSchema.optional(),
  client_id: z.string(),
  has_client_secret: z.boolean().optional(),
  token_endpoint_auth: tokenEndpointAuthSchema.optional(),
  jwks_uri: z.string().optional(),
  require_pkce: z.boolean().optional(),
  /** SP-side URL the user portal opens when the operator clicks
   *  "Launch" on the app tile or the detail-page action bar.
   *  Server-side type is `string` with `omitempty` (empty string
   *  serialises as absent), so we accept both states. The Launch
   *  affordance hides when this is missing — a tenant created via
   *  an older path that didn't populate it should not show a
   *  broken Launch link. */
  launch_url: z.string().optional(),
  redirect_uris: z.array(z.string()),
  allowed_scopes: z.array(z.string()),
  entity_id: z.string().nullable(),
  acs_url: z.string().nullable(),
  /** The Subject NameID format this SP receives.
   *
   * Kept as the closed enum rather than a bare string: this value decides
   * what identifier the SP keys accounts on, so a typo is an account-level
   * incident and the schema is the cheapest place to catch it.
   *
   * `.optional()` because the backend omits the field for rows that have
   * none; `.catch(null)` guards a row written before migration 000010 or by
   * a tool that bypassed it. Either way an unknown or absent URI degrades to
   * "not set" — which the detail page renders as a prompt to choose — rather
   * than failing the whole parse and blanking the page the operator opened
   * to fix exactly that. */
  saml_name_id_format: samlNameIDFormatSchema.nullable().optional().catch(null),
  /** NameID value source: a literal user-profile key (e.g. "email")
   * or an "${...}"-wrapped expression, resolved per-user at
   * assertion time. Omitted means "use the built-in default". */
  saml_name_id_source: z.string().optional(),
  /** Read-only. The format this application carried before migration
   *  000010 backfilled every SAML row to persistent. Present only when
   *  the operator had configured something else, which never took
   *  effect — the pre-000010 assertion path ignored the column and
   *  sent an opaque id regardless. The detail page surfaces it once. */
  saml_name_id_format_prior: z.string().optional(),
  /** Standard attribute Names this SP must not receive. */
  saml_suppressed_attributes: z.array(z.string()).optional(),
  /** SAML SP signing certificate (PEM-encoded X.509). Used by the
   * canonical SAML IdP to verify signatures on incoming
   * AuthnRequest and LogoutRequest payloads. NULL until an
   * operator pastes the SP's cert. SLO is rejected without one;
   * AuthnRequest is rejected when
   * saml_require_signed_authn_request is true. See backend T20. */
  saml_sp_signing_cert: z.string().nullable(),
  /** When true, the IdP rejects unsigned AuthnRequest payloads
   * with 400. Defaults to true server-side because modern SPs
   * sign — operators integrating with legacy SPs that refuse
   * to sign can flip this off per-app. SLO ignores this flag
   * (always signature-required). */
  saml_require_signed_authn_request: z.boolean(),
  /** SP's Single Logout Service URL (HTTP-POST binding). When set,
   * the IdP sends a signed LogoutResponse to this URL after
   * processing a LogoutRequest, and the IdP metadata advertises
   * a <SingleLogoutService> element. Null when not configured. */
  saml_slo_url: z.string().nullable().optional(),
  /** Default RelayState for IdP-initiated SAML SSO. When set, sent as
   * RelayState in unsolicited Responses. launch_url is a portal tile
   * link, not a SAML protocol value. Null = blank RelayState. */
  saml_default_relay_state: z.string().nullable().optional(),
  /** Overrides the <Audience> value in SAML assertions. Default: entity_id.
   * Use when the SP expects a different audience URI from its entity_id
   * (e.g. AWS SAML). Null = use entity_id. */
  saml_audience_override: z.string().nullable().optional(),
  /** Overrides SubjectConfirmationData Recipient URL. Default: ACS URL.
   * Null = use the registered ACS URL. */
  saml_recipient_override: z.string().nullable().optional(),
  status: applicationStatusSchema,
  configuration_revision: z.number().int().positive(),
  enablement_exception_revision: z.number().int().positive().optional(),
  enablement_exception_reason: z.string().optional(),
  enablement_exception_approved_by: z.string().uuid().optional(),
  enablement_exception_expires_at: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  /** has_icon is derived server-side from `icon_data IS NOT NULL`.
   *  icon_updated_at is emitted only when has_icon is true; the SPA
   *  uses it as a cache-bust `?v=` suffix on the served-icon URL
   *  so a re-upload replaces the preview immediately even though
   *  the endpoint path is stable. */
  has_icon: z.boolean(),
  icon_updated_at: z.string().optional(),
  /** Client secret — only present on create response, never in reads. */
  client_secret: z.string().nullable().optional(),
  /** SMART-on-FHIR fields. Populated only on smart_on_fhir tiles.
   * `smart_issuer_url` is the FHIR base URL the launch handler
   * runs SMART discovery against. `smart_client_id` is PureID's
   * OAuth2 client identifier registered with the EHR.
   * `smart_scopes` is the OAuth scope set requested on launch.
   * All three are emitted with `omitempty` on the backend so a
   * non-SMART row carries them as undefined. */
  smart_issuer_url: z.string().optional(),
  smart_client_id: z.string().optional(),
  smart_scopes: z.array(z.string()).optional(),
  /** SAML attribute-value mapping. Key = the SAML attribute Name to
   * emit; value = either a literal source key on the user (e.g.
   * "department", "email") or a "${...}"-wrapped expression
   * evaluated against the user (see internal/platform/exprlang on
   * the backend). Omitted (`omitempty`) when the app has none
   * configured. See db/migrations/000001_init.up.sql. */
  saml_attribute_map: z.record(z.string(), z.string()).optional(),
});
export type Application = z.infer<typeof applicationSchema>;

export const applicationsListResponseSchema = z.object({
  data: z.array(applicationSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    // nonnegative (not positive) — a backend that returns 0 here
    // carries less information but is not a reason to kill the
    // whole page with a Zod throw that then goes through the
    // TanStack retry loop and logs three phantom failures. The
    // rendered table does not branch on limit; it's a hint only.
    limit: z.number().int().nonnegative(),
  }),
});
export type ApplicationsListResponse = z.infer<typeof applicationsListResponseSchema>;

// ── Usage (stats displayed inline in the list) ────────────────────────────

export type AppUsageMap = Record<
  string,
  { unique_users: number; last_accessed_at: string | null }
>;

// ── URL validation primitives ─────────────────────────────────────────────
//
// Mirrors the backend's RFC 8252 §7.3 redirect URI rules + the SAML
// service's ACS URL invariants:
//
//   * absolute URL with explicit scheme + host
//   * scheme MUST be https; loopback is the ONLY http carve-out
//     (localhost / 127.0.0.1 / ::1 — for local development only)
//   * no fragments (the OAuth response already encodes #access_token=… for
//     the implicit flow; redirecting to a URL that already carries a
//     fragment is broken-by-design)
//   * no whitespace anywhere in the value (the most common
//     copy-paste failure mode)
//   * no userinfo (https://user:pass@host) — credentials in URLs are
//     a leak waiting to happen and the spec deprecated them anyway

/**
 * Strip surrounding whitespace and collapse a single trailing slash
 * on the *origin*. We do NOT strip every trailing slash because a
 * legitimate path-only callback (`/auth/callback/`) MAY end in one
 * — only origin-only URLs (`https://host/`) get the slash dropped.
 */
export function normalizeUrlForCompare(input: string): string {
  const trimmed = input.trim();
  // Origin-only with a single trailing slash: drop it for stable
  // comparison. We deliberately do not lowercase the path, query,
  // or anchor — those segments are case-sensitive on most servers.
  // Lowercasing the scheme is safe (RFC 3986 §3.1) and protects
  // against `HTTPS://` mismatch failures.
  try {
    const parsed = new URL(trimmed);
    const lcScheme = parsed.protocol.toLowerCase();
    let normalized = `${lcScheme}//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
    // If the path is exactly "/" and there's no query/hash, drop the slash.
    if (parsed.pathname === "/" && !parsed.search && !parsed.hash) {
      normalized = `${lcScheme}//${parsed.host}`;
    }
    return normalized;
  } catch {
    return trimmed;
  }
}

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);
const LOCALDEV_JWKS_HTTP_HOSTS = new Set([
  "host.docker.internal",
  "localhost",
  "127.0.0.1",
  "[::1]",
  "::1",
]);

interface UrlIssue {
  message: string;
}

/**
 * Strict URL validator used for OIDC redirect URIs and SAML ACS URLs.
 * Returns the parsed URL on success, or a UrlIssue describing the
 * single failure mode (we surface one error per field; piling on
 * five issues for a single bad URL is noise).
 */
export function checkSpUrl(input: string, opts: { label: string }): URL | UrlIssue {
  const raw = input;
  if (!raw || raw.trim() !== raw) {
    return { message: `${opts.label} must not contain leading or trailing whitespace.` };
  }
  if (/\s/.test(raw)) {
    return { message: `${opts.label} contains whitespace.` };
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { message: `${opts.label} is not a valid absolute URL (e.g. https://app.example.com/callback).` };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return { message: `${opts.label} must use the https:// scheme (got "${url.protocol}").` };
  }
  if (url.protocol === "http:" && !LOOPBACK_HOSTS.has(url.hostname)) {
    return { message: `${opts.label} must use https — http is only allowed for localhost / 127.0.0.1 / ::1.` };
  }
  if (url.username || url.password) {
    return { message: `${opts.label} must not embed credentials (no user:pass@ syntax).` };
  }
  if (url.hash) {
    return { message: `${opts.label} must not contain a #fragment.` };
  }
  if (url.hostname.includes("*")) {
    return { message: `${opts.label} must not contain wildcards — register each callback explicitly.` };
  }
  return url;
}

function checkClientJwksUrl(input: string): URL | UrlIssue {
  const raw = input;
  if (!raw || raw.trim() !== raw) {
    return { message: "JWKS URI must not contain leading or trailing whitespace." };
  }
  if (/\s/.test(raw)) {
    return { message: "JWKS URI contains whitespace." };
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { message: "JWKS URI is not a valid absolute URL (e.g. https://app.example.com/jwks)." };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return { message: `JWKS URI must use the https:// scheme (got "${url.protocol}").` };
  }
  if (url.protocol === "http:" && !LOCALDEV_JWKS_HTTP_HOSTS.has(url.hostname)) {
    return {
      message:
        "JWKS URI must use https, except localdev host.docker.internal or loopback HTTP.",
    };
  }
  if (url.username || url.password) {
    return { message: "JWKS URI must not embed credentials (no user:pass@ syntax)." };
  }
  if (url.hash) {
    return { message: "JWKS URI must not contain a #fragment." };
  }
  if (url.hostname.includes("*")) {
    return { message: "JWKS URI must not contain wildcards." };
  }
  return url;
}

/** Returns true if the cert blob looks like a PEM X.509. The
 *  authoritative parse happens server-side; this is a typo
 *  catcher (paste-the-private-key / paste-just-the-base64).  */
export function looksLikePemCert(input: string): boolean {
  const v = input.trim();
  if (!v) return false;
  return /-----BEGIN CERTIFICATE-----[\s\S]+-----END CERTIFICATE-----\s*$/i.test(v);
}

/**
 * Entity ID validator. The SAML 2.0 spec (sstc-saml-core §8.3.6)
 * accepts any URI ≤ 1024 chars; in practice every IdP ships a URL
 * but URN entity IDs are valid too (e.g. `urn:oasis:names:tc:...`).
 * Reject whitespace + zero-length and cap at the spec ceiling.
 */
function checkEntityId(input: string): UrlIssue | null {
  const v = input;
  if (!v || v.trim() !== v) return { message: "Entity ID must not contain leading or trailing whitespace." };
  if (v.length === 0) return { message: "Entity ID is required." };
  if (v.length > 1024) return { message: "Entity ID exceeds the 1024-char SAML ceiling." };
  if (/\s/.test(v)) return { message: "Entity ID must not contain whitespace." };
  // Must be a URI: either http(s):// or a urn: prefix. Operators
  // occasionally paste the SP's display name here by mistake — fail
  // fast with a precise message rather than letting the server 400
  // with a less obvious error.
  if (!/^(https?:\/\/|urn:)/.test(v)) {
    return { message: 'Entity ID must be a URI — typically "https://…" or "urn:…".' };
  }
  return null;
}

/**
 * Derive a sensible launch_url from the protocol-specific identifiers
 * the operator already typed. The launch URL is what the user portal
 * navigates to when the user clicks the app tile, and what the detail
 * page's Launch button opens — typically the SP's home/origin, since
 * SP-initiated SSO bounces off that origin and lands the user in the
 * authenticated app.
 *
 * OIDC: origin of the first valid redirect URI. The redirect URI
 *       points at the OIDC client's callback, which is hosted on
 *       the SP's origin — exactly what we want.
 *
 * SAML: origin of the entity_id (when it's a URL) else origin of
 *       the ACS URL. URN entity_ids never give us an origin; we
 *       fall back to the ACS URL in that case.
 *
 * Returns null when no candidate input is parseable — the create
 * call still succeeds (backend stores empty), the Launch button
 * just won't appear.
 */
export function deriveLaunchUrl(input: {
  protocol: Protocol;
  redirect_uris?: readonly string[];
  entity_id?: string;
  acs_url?: string;
}): string | null {
  function originOf(raw: string | undefined): string | null {
    if (!raw) return null;
    try {
      const u = new URL(raw.trim());
      // WHATWG URL returns the literal string "null" for the
      // .origin of opaque-origin schemes (urn:, data:, file:,
      // anything not http(s)/ws(s)/ftp). That is NOT a launchable
      // value — treat it as no-origin so the caller falls through
      // to the next candidate (e.g. SAML's acs_url after a URN
      // entity_id). Only http/https are valid SP-launch origins.
      if (u.protocol !== "http:" && u.protocol !== "https:") return null;
      if (u.origin === "null") return null;
      return u.origin;
    } catch {
      return null;
    }
  }
  if (input.protocol === "oidc") {
    for (const u of input.redirect_uris ?? []) {
      const o = originOf(u);
      if (o) return o;
    }
    return null;
  }
  // SAML
  return originOf(input.entity_id) ?? originOf(input.acs_url);
}

// ── SAML attribute map (editable form shape) ───────────────────────────────
//
// The wire shape is a plain Record<string, string> (see
// applicationSchema.saml_attribute_map above) — but a Record can't
// represent "two rows both currently named ''" while an operator is
// mid-edit (JS object keys collapse duplicates immediately). The form
// instead edits an ARRAY of {name, source} entries — the same
// per-row-addressable-by-index pattern already used for
// redirect_uris/allowed_scopes — and we convert to/at the Record only
// at the read (appToFormValues) and write (mutation) boundaries.

export const samlAttributeMapEntrySchema = z.object({
  name: z.string(),
  source: z.string(),
});
export type SamlAttributeMapEntry = z.infer<typeof samlAttributeMapEntrySchema>;

export function attributeMapToEntries(
  m: Record<string, string> | undefined,
): SamlAttributeMapEntry[] {
  return Object.entries(m ?? {}).map(([name, source]) => ({ name, source }));
}

export function entriesToAttributeMap(
  entries: SamlAttributeMapEntry[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of entries) {
    out[e.name] = e.source;
  }
  return out;
}

// ── Mutation inputs ───────────────────────────────────────────────────────

export const createApplicationSchema = z
  .object({
    destination_scope: destinationScopeSchema.optional(),
    name: z.string().min(1, "Name is required").max(80, "Name too long (max 80)"),
    protocol: protocolSchema,
    /** OIDC: application type determines client_type and PKCE behaviour.
     *  Required when protocol === "oidc". Defaults to "web" (confidential). */
    application_type: applicationTypeSchema.optional(),
    /** OIDC: the scopes this client is permitted to request. At least
     *  "openid" is required for OIDC flows. Pre-seeded with sensible
     *  defaults in the create dialog. */
    allowed_scopes: z.array(z.string()).optional(),
    /** OIDC: PKCE enforcement flag. Forced true (non-configurable) for
     *  public clients (spa/native). Configurable for web (confidential). */
    require_pkce: z.boolean().optional(),
    /** OIDC confidential clients: token endpoint authentication method.
     *  `private_key_jwt` requires jwks_uri and carries no shared secret. */
    token_endpoint_auth: tokenEndpointAuthSchema.optional(),
    /** Client JWKS URI used only when token_endpoint_auth=private_key_jwt. */
    jwks_uri: z.string().optional(),
    // We accept any string list here and run the strict per-URL
    // check inside superRefine so error paths can target the
    // specific index that failed (`redirect_uris.0`, .1, .2…).
    redirect_uris: z.array(z.string()),
    entity_id: z.string().optional(),
    acs_url: z.string().optional(),
    /** SAML: the Subject NameID format. Optional on the wire (the backend
     *  materialises DefaultSAMLNameIDFormat into the row when absent), but
     *  the create dialog always sends it explicitly and seeds the control
     *  with DEFAULT_SAML_NAME_ID_FORMAT — an operator should not have to
     *  discover after the fact which identifier their SP received. */
    saml_name_id_format: samlNameIDFormatSchema.optional(),
    /** SAML: the service provider's signing certificate, PEM.
     *
     *  Accepted at create because discovery already reads it out of
     *  the SP's metadata and the API already accepts it — it was
     *  parsed, displayed in the discovery preview as if captured,
     *  and then dropped on the floor, so the operator had to paste
     *  it again on the detail page before signed AuthnRequests or
     *  Single Logout would work at all. Optional: an SP that has
     *  not generated its keypair yet is the normal first-day
     *  state. */
    saml_sp_signing_cert: z.string().optional(),
    /** SAML: whether PureID refuses an AuthnRequest that is not
     *  validly signed.
     *
     *  Sent EXPLICITLY, because the backend's default is derived
     *  from certificate presence — supplying a certificate silently
     *  turns signing from optional into mandatory. That was
     *  invisible until this form could supply a certificate at all;
     *  now that discovery fills one in automatically, an SP that
     *  publishes a signing key for Single Logout but does not sign
     *  its AuthnRequests would have every sign-in rejected, with
     *  nothing on screen having said so. */
    saml_require_signed_authn_request: z.boolean().optional(),
    /** SAML: the SP's Single Logout Service URL (HTTP-POST binding).
     *  Optional. When set, the IdP sends a signed LogoutResponse
     *  here after processing a LogoutRequest and advertises the
     *  IdP SLO endpoint in metadata. Discovery may pre-fill this
     *  from the SP's SAML metadata <SingleLogoutService>. */
    saml_slo_url: z.string().optional(),
    /** Default RelayState for IdP-initiated SAML SSO. Optional. */
    saml_default_relay_state: z.string().optional(),
    /** Overrides <Audience> in SAML assertions. Default: entity_id. */
    saml_audience_override: z.string().optional(),
    /** Overrides SubjectConfirmationData Recipient. Default: ACS URL. */
    saml_recipient_override: z.string().optional(),
    /** Optional on create. The dialog auto-derives via
     *  deriveLaunchUrl() when omitted, so the Launch button works
     *  on every newly-created app. Operators who want a different
     *  target (e.g. /login instead of /) edit it on the detail
     *  page via updateApplicationSchema's launch_url field. */
    launch_url: z.string().optional(),
    /** SMART-on-FHIR fields. Required iff protocol === "smart_on_fhir".
     * `smart_issuer_url` is the FHIR base URL; `smart_client_id` is
     * the OAuth client id PureID registered with the EHR;
     * `smart_scopes` defaults to DEFAULT_SMART_SCOPES when the
     * operator doesn't override. The superRefine below enforces
     * presence + shape for the smart_on_fhir branch. */
    smart_issuer_url: z.string().optional(),
    smart_client_id: z.string().optional(),
    smart_scopes: z.array(z.string()).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.destination_scope && value.protocol !== "private_access") {
      ctx.addIssue({ code: "custom", path: ["destination_scope"], message: "Destination scope is available only for private-access applications." });
    }
    if (value.protocol === "oidc") {
      // application_type is required for OIDC apps.
      if (!value.application_type) {
        ctx.addIssue({
          code: "custom",
          message: "Select an application type.",
          path: ["application_type"],
        });
      }
      // allowed_scopes: require at least openid.
      const scopes = (value.allowed_scopes ?? []).filter((s) => s.length > 0);
      if (scopes.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "At least one scope is required. Add 'openid' as a minimum.",
          path: ["allowed_scopes"],
        });
      }
      scopes.forEach((s, idx) => {
        if (/\s/.test(s)) {
          ctx.addIssue({
            code: "custom",
            message: "Scopes must not contain whitespace.",
            path: ["allowed_scopes", idx],
          });
        }
      });
      const filled = value.redirect_uris.filter((u) => u.length > 0);
      if (filled.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "OIDC applications need at least one redirect URI.",
          path: ["redirect_uris"],
        });
      }
      // Validate each URL individually so the error surfaces on
      // the failing row, not as a single error blob on the array.
      value.redirect_uris.forEach((uri, idx) => {
        if (uri.length === 0) return;  // empty rows are user-pending; gate on length count above.
        const result = checkSpUrl(uri, { label: "Redirect URI" });
        if ("message" in result) {
          ctx.addIssue({ code: "custom", message: result.message, path: ["redirect_uris", idx] });
        }
      });
      // Duplicate detection — registering the same URI twice is
      // never intentional and makes future revocation ambiguous.
      const seen = new Set<string>();
      value.redirect_uris.forEach((uri, idx) => {
        if (!uri) return;
        const norm = normalizeUrlForCompare(uri);
        if (seen.has(norm)) {
          ctx.addIssue({
            code: "custom",
            message: "Duplicate redirect URI (same value as another row).",
            path: ["redirect_uris", idx],
          });
        }
        seen.add(norm);
      });
      if (value.token_endpoint_auth === "private_key_jwt") {
        if (value.application_type !== "web") {
          ctx.addIssue({
            code: "custom",
            message: "Private key JWT is only valid for confidential web applications.",
            path: ["token_endpoint_auth"],
          });
        }
        const jwks = (value.jwks_uri ?? "").trim();
        if (jwks.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: "JWKS URI is required for private key JWT.",
            path: ["jwks_uri"],
          });
        } else {
          const r = checkClientJwksUrl(jwks);
          if ("message" in r) {
            ctx.addIssue({ code: "custom", message: r.message, path: ["jwks_uri"] });
          }
        }
      }
    }
    if (value.protocol === "saml") {
      const eid = value.entity_id ?? "";
      const eidIssue = checkEntityId(eid);
      if (eidIssue) {
        ctx.addIssue({ code: "custom", message: eidIssue.message, path: ["entity_id"] });
      }
      const acs = value.acs_url ?? "";
      if (acs.length === 0) {
        ctx.addIssue({ code: "custom", message: "ACS URL is required for SAML applications.", path: ["acs_url"] });
      } else {
        const acsResult = checkSpUrl(acs, { label: "ACS URL" });
        if ("message" in acsResult) {
          ctx.addIssue({ code: "custom", message: acsResult.message, path: ["acs_url"] });
        }
      }
      // Same PEM shape check the edit form applies. Catching it
      // here means a mis-paste is a red field, not a 400 from the
      // API after the operator has already left the dialog.
      const cert = (value.saml_sp_signing_cert ?? "").trim();
      if (cert.length > 0 && !looksLikePemCert(cert)) {
        ctx.addIssue({
          code: "custom",
          message:
            "That does not look like a PEM certificate. Paste the block beginning -----BEGIN CERTIFICATE-----.",
          path: ["saml_sp_signing_cert"],
        });
      }
      const sloUrl = (value.saml_slo_url ?? "").trim();
      if (sloUrl.length > 0) {
        const r = checkSpUrl(sloUrl, { label: "SP SLO URL" });
        if ("message" in r) {
          ctx.addIssue({ code: "custom", message: r.message, path: ["saml_slo_url"] });
        }
      }
    }
    if (value.protocol === "smart_on_fhir") {
      const iss = (value.smart_issuer_url ?? "").trim();
      if (iss.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "FHIR issuer URL is required for SMART tiles.",
          path: ["smart_issuer_url"],
        });
      } else {
        // SMART issuer URL: same strict rules as a redirect URI
        // (absolute https, no fragment, no userinfo, no whitespace),
        // plus a "no query string" rule because the discovery fetch
        // appends /.well-known/smart-configuration directly.
        const r = checkSpUrl(iss, { label: "FHIR issuer URL" });
        if ("message" in r) {
          ctx.addIssue({ code: "custom", message: r.message, path: ["smart_issuer_url"] });
        } else if (r.search.length > 0) {
          ctx.addIssue({
            code: "custom",
            message: "FHIR issuer URL must not include a query string.",
            path: ["smart_issuer_url"],
          });
        } else if (r.protocol !== "https:") {
          ctx.addIssue({
            code: "custom",
            message: "FHIR issuer URL must use https (loopback http is not allowed for SMART).",
            path: ["smart_issuer_url"],
          });
        }
      }
      const clientID = (value.smart_client_id ?? "").trim();
      if (clientID.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "OAuth client ID is required for SMART tiles.",
          path: ["smart_client_id"],
        });
      }
      const scopes = value.smart_scopes ?? [];
      if (scopes.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "At least one SMART scope is required.",
          path: ["smart_scopes"],
        });
      } else {
        scopes.forEach((s, idx) => {
          if (s.trim().length === 0 || /\s/.test(s)) {
            ctx.addIssue({
              code: "custom",
              message: "Scopes must be non-empty and contain no whitespace.",
              path: ["smart_scopes", idx],
            });
          }
        });
      }
    }
  });
/**
 * The rotate-secret response. A one-time reveal, same single-use
 * semantics as the plaintext on create: this value is returned by
 * the mutation and never written to any cache.
 */
export const rotateSecretResponseSchema = z.object({
  client_secret: z.string().min(1),
});

export type CreateApplicationInput = z.infer<typeof createApplicationSchema>;

// ── Update (edit) input ───────────────────────────────────────────────────
// The edit form is flat and does not re-ask for the protocol — a
// live app can't change from OIDC to SAML without a destructive
// re-create. Protocol-specific fields are still optional here so
// both variants share one schema.
export const updateApplicationSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(80, "Name too long (max 80)"),
    // Same loose-then-refine pattern as createApplicationSchema —
    // the strict per-URL checks live in superRefine so error
    // paths target the failing row.
    redirect_uris: z.array(z.string()),
    allowed_scopes: z.array(z.string().min(1, "Scope cannot be empty")),
    /** PKCE enforcement. Omitted from the payload for public clients
     *  (the backend always forces it on; the UI shows it disabled). */
    require_pkce: z.boolean().optional(),
    entity_id: z.string().nullable(),
    acs_url: z.string().nullable(),
    /** Literal user-attribute key from the CLOSED set the backend
     * accepts (email / username / employee_id / custom_<key>), or ""
     * to let the format decide. Expressions are NOT permitted here —
     * see the superRefine below and
     * application.ValidateSAMLNameIDSource for why: no expression can
     * be guaranteed unique per user, and a colliding name identifier
     * merges two people into one account at the service provider. */
    saml_name_id_source: z.string(),
    /** PEM-encoded X.509. Empty string is permitted and clears
     * the cert; non-empty must look like a PEM block. The
     * authoritative parse + validation happens server-side in
     * scanSP — we only catch obvious typos here. */
    saml_sp_signing_cert: z.string().nullable(),
    saml_require_signed_authn_request: z.boolean(),
    /** SP's Single Logout Service URL (HTTP-POST binding).
     * Empty string clears the URL. When set, the IdP sends a
     * signed LogoutResponse here after processing a
     * LogoutRequest and advertises a <SingleLogoutService>
     * element in the IdP metadata. */
    saml_slo_url: z.string(),
    /** Default RelayState for IdP-initiated SAML SSO. Empty string clears. */
    saml_default_relay_state: z.string(),
    /** Overrides <Audience> in SAML assertions. Empty string clears (reverts to entity_id). */
    saml_audience_override: z.string(),
    /** Overrides SubjectConfirmationData Recipient. Empty string clears (reverts to ACS URL). */
    saml_recipient_override: z.string(),
    /** SAML NameID format.
     *
     *  Optional, and the optionality is protocol scoping rather than laxity:
     *  the detail form PATCHes its whole value object, so an OIDC or SMART app
     *  must omit this field entirely or it stamps a SAML-only policy value
     *  onto a row that never consults it. `undefined` drops out of the JSON
     *  body and the backend leaves the column alone.
     *
     *  When PRESENT it must be a supported format — never an empty string. The
     *  backend accepts a change but never a clear, because clearing leaves a
     *  live app without an explicit identifier contract and the
     *  breakage would surface at the next sign-in rather than at the edit. */
    saml_name_id_format: samlNameIDFormatSchema.optional(),
    /** Editable-array form of saml_attribute_map — see the "SAML
     * attribute map" section above for why this isn't a Record.
     * Converted to the wire Record<string,string> at mutation time
     * (entriesToAttributeMap). */
    saml_attribute_map: z.array(samlAttributeMapEntrySchema),
    /** Empty string clears the launch URL (Launch button hides
     *  on the detail page and portal tile becomes a no-op tap).
     *  Non-empty must satisfy the same SP-URL rules we apply to
     *  redirect URIs (https-only with localhost carve-out, no
     *  fragments, no userinfo, no whitespace). The same rules
     *  also block javascript:/data: schemes that an
     *  XSS-via-launch-button vector would rely on. */
    launch_url: z.string(),
    // status is intentionally NOT part of the general update — the
    // backend rejects a status field here and requires the dedicated
    // PATCH /applications/{id}/status lifecycle endpoint (see
    // useSetApplicationStatusMutation) so the active ↔ disabled
    // transition emits its own audit event.
  })
  .superRefine((value, ctx) => {
    // Per-URI validation, mirrors createApplicationSchema. We don't
    // know the protocol here (it's not on UpdateApplicationInput;
    // immutable post-create), but the rules are protocol-aware
    // server-side: redirect_uris are only enforced for OIDC, ACS for
    // SAML. We validate whatever the caller submitted — empty arrays
    // are a no-op, populated arrays must be strict.
    value.redirect_uris.forEach((uri, idx) => {
      if (uri.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Redirect URI cannot be empty — remove the row instead.",
          path: ["redirect_uris", idx],
        });
        return;
      }
      const r = checkSpUrl(uri, { label: "Redirect URI" });
      if ("message" in r) {
        ctx.addIssue({ code: "custom", message: r.message, path: ["redirect_uris", idx] });
      }
    });
    const seen = new Set<string>();
    value.redirect_uris.forEach((uri, idx) => {
      if (!uri) return;
      const norm = normalizeUrlForCompare(uri);
      if (seen.has(norm)) {
        ctx.addIssue({
          code: "custom",
          message: "Duplicate redirect URI.",
          path: ["redirect_uris", idx],
        });
      }
      seen.add(norm);
    });
    if (value.entity_id !== null && value.entity_id.length > 0) {
      const eidIssue = checkEntityId(value.entity_id);
      if (eidIssue) {
        ctx.addIssue({ code: "custom", message: eidIssue.message, path: ["entity_id"] });
      }
    }
    if (value.acs_url !== null && value.acs_url.length > 0) {
      const r = checkSpUrl(value.acs_url, { label: "ACS URL" });
      if ("message" in r) {
        ctx.addIssue({ code: "custom", message: r.message, path: ["acs_url"] });
      }
    }
    if (value.saml_name_id_source.trim() !== value.saml_name_id_source) {
      ctx.addIssue({
        code: "custom",
        message: "Name identifier source must not contain leading or trailing whitespace.",
        path: ["saml_name_id_source"],
      });
    }
    // The same closed vocabulary the backend enforces. Mirrored here
    // so the operator is told at the point of typing rather than by a
    // 400 after they hit save — and worded to say WHY, because
    // "invalid value" leaves them guessing that `department` should
    // have worked.
    const nameIDSource = value.saml_name_id_source.trim();
    if (nameIDSource.length > 0 && value.saml_name_id_format === "urn:oasis:names:tc:SAML:2.0:nameid-format:transient") {
      ctx.addIssue({code: "custom", message: "Transient identifiers cannot use a permanent source.", path: ["saml_name_id_source"]});
    }
    if (nameIDSource.length > 0) {
      if (nameIDSource.startsWith("${")) {
        ctx.addIssue({
          code: "custom",
          message:
            "Expressions are not allowed for the name identifier: an expression cannot be guaranteed unique per user, and two users sharing one would share an account at this application.",
          path: ["saml_name_id_source"],
        });
      } else if (!isValidNameIDSource(nameIDSource)) {
        ctx.addIssue({
          code: "custom",
          message:
            "Must identify a single user: email, employee_id, or a tenant attribute kept unique and set once.",
          path: ["saml_name_id_source"],
        });
      } else if (
        value.saml_name_id_format === SAML_NAME_ID_FORMAT_EMAIL &&
        nameIDSource !== "email"
      ) {
        // The emailAddress format promises an RFC 2822 address
        // (SAML Core §8.3.2). Anything else under it is a value of
        // the wrong kind, which is the same violation as
        // substituting an opaque id — reached by configuration.
        ctx.addIssue({
          code: "custom",
          message:
            'The "Email address" name identifier format requires the email source. Choose a different format, or set the source to email.',
          path: ["saml_name_id_source"],
        });
      }
    }
    if (value.saml_sp_signing_cert !== null && value.saml_sp_signing_cert.length > 0) {
      if (!looksLikePemCert(value.saml_sp_signing_cert)) {
        ctx.addIssue({
          code: "custom",
          message: 'SP signing certificate must be a PEM block ("-----BEGIN CERTIFICATE-----" … "-----END CERTIFICATE-----").',
          path: ["saml_sp_signing_cert"],
        });
      }
    }
    // Empty string clears the SLO URL (valid). Non-empty must be a
    // strict https URL for the same reasons as the ACS URL.
    if (value.saml_slo_url.length > 0) {
      const r = checkSpUrl(value.saml_slo_url, { label: "SP SLO URL" });
      if ("message" in r) {
        ctx.addIssue({ code: "custom", message: r.message, path: ["saml_slo_url"] });
      }
    }
    // Empty launch_url is the legitimate "clear it" path — no
    // validation. Non-empty values run through the same strict
    // SP-URL checker as redirect URIs (https-only with the
    // documented localhost carve-out, no fragments, no userinfo).
    // This is also the security boundary that blocks editing the
    // launch_url to a javascript: scheme — checkSpUrl rejects
    // anything that isn't http/https, which keeps the
    // <a href={launch_url}> on the detail page from becoming an
    // XSS vector for an operator with edit-without-create rights.
    if (value.launch_url.length > 0) {
      const r = checkSpUrl(value.launch_url, { label: "Launch URL" });
      if ("message" in r) {
        ctx.addIssue({ code: "custom", message: r.message, path: ["launch_url"] });
      }
    }
    // Mirrors the backend's fail-closed ValidateSAMLAttributeMap:
    // neither the attribute Name nor its source may be blank. Full
    // "${...}" expression-syntax validation only happens server-side
    // (it needs the exprlang parser) — a malformed expression surfaces
    // as a save-time toast, not an inline error here.
    value.saml_attribute_map.forEach((entry, idx) => {
      if (entry.name.trim().length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Attribute name cannot be empty — remove the row instead.",
          path: ["saml_attribute_map", idx, "name"],
        });
      }
      if (entry.source.trim().length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Source cannot be empty — remove the row instead.",
          path: ["saml_attribute_map", idx, "source"],
        });
      }
    });
    const seenAttrNames = new Set<string>();
    value.saml_attribute_map.forEach((entry, idx) => {
      if (!entry.name) return;
      if (seenAttrNames.has(entry.name)) {
        ctx.addIssue({
          code: "custom",
          message: "Duplicate attribute name (same value as another row).",
          path: ["saml_attribute_map", idx, "name"],
        });
      }
      seenAttrNames.add(entry.name);
    });
    // Cross-field guard mirrors the backend invariant: requiring
    // signed AuthnRequest without a cert is impossible to satisfy
    // (every request would be rejected). The server enforces this
    // via a CHECK constraint; catching it here gives the operator
    // an immediate inline error instead of a 400 toast.
    if (value.saml_require_signed_authn_request) {
      const cert = value.saml_sp_signing_cert ?? "";
      if (cert.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: 'Cannot require signed AuthnRequest without an SP signing certificate. Paste the SP cert above first, or turn this off.',
          path: ["saml_require_signed_authn_request"],
        });
      }
    }
  });
export type UpdateApplicationInput = z.infer<typeof updateApplicationSchema>;

// Per-app JIT request policy. Backed by application_request_policy
// (sidecar table). Defaults are permissive; operators tighten per
// app for sensitive surfaces or auto-approve carve-outs.
// The response is defined by the API contract (ApplicationRequestPolicy) and generated.
export { accessPolicySchema };
export type AccessPolicy = z.infer<typeof accessPolicySchema>;

// ── Discovery ─────────────────────────────────────────────────────────────
//
// Wire shape for POST /api/v1/tenants/{tenant_id}/applications/discover.
// Mirrors backend pkg/api/tenant/application.go DiscoverApplication*
// types one-to-one. The dialog feeds the response into the form's
// defaultValues; nothing here is persisted until the operator clicks
// Create on the (now pre-filled) form.

export const discoverSamlMetadataSchema = z.object({
  source_url: z.string(),
  entity_id: z.string(),
  acs_url: z.string(),
  acs_binding: z.string().optional(),
  slo_url: z.string().optional(),
  sp_signing_certificate: z.string().optional(),
  nameid_formats: z.array(z.string()).optional(),
  want_assertions_signed: z.boolean(),
  want_authn_request_signed: z.boolean(),
  signed: z.boolean(),
});
export type DiscoverSamlMetadata = z.infer<typeof discoverSamlMetadataSchema>;

export const discoverOidcConfigSchema = z.object({
  source_url: z.string(),
  issuer: z.string(),
  authorization_endpoint: z.string().optional(),
  token_endpoint: z.string().optional(),
  userinfo_endpoint: z.string().optional(),
  jwks_uri: z.string().optional(),
  scopes_supported: z.array(z.string()).optional(),
});
export type DiscoverOidcConfig = z.infer<typeof discoverOidcConfigSchema>;

export const probeOutcomeSchema = z.enum(["success", "expected_miss", "failure"]);
export type ProbeOutcome = z.infer<typeof probeOutcomeSchema>;

export const discoverProbeRecordSchema = z.object({
  url: z.string(),
  kind: z.string(),
  status: z.number().int().optional(),
  note: z.string().optional(),
  // outcome is the semantic classification — "success" / "expected_miss"
  // (routine 404 on optional path) / "failure". The status code alone
  // can't distinguish a 404 on /Shibboleth.sso/Metadata (normal) from
  // a 200 with malformed XML (broken SP). Backend always populates it.
  outcome: probeOutcomeSchema,
});
export type DiscoverProbeRecord = z.infer<typeof discoverProbeRecordSchema>;

export const discoverApplicationResponseSchema = z.object({
  canonical_url: z.string(),
  display_name: z.string().optional(),
  matched_template: z.string().optional(),
  favicon_data_url: z.string().optional(),
  saml_metadata: discoverSamlMetadataSchema.nullable().optional(),
  oidc_discovery: discoverOidcConfigSchema.nullable().optional(),
  probes: z.array(discoverProbeRecordSchema),
});
export type DiscoverApplicationResponse = z.infer<typeof discoverApplicationResponseSchema>;

// ── Preflight ────────────────────────────────────────────────────────────
//
// The preflight endpoint runs deterministic configuration checks
// against an existing application row and returns a structured
// checklist. The frontend renders one row per check, sorted with
// errors first; the summary counts in the envelope drive the
// "2 errors, 1 warning" header.

export const preflightSeveritySchema = z.enum(["ok", "warning", "error"]);
export type PreflightSeverity = z.infer<typeof preflightSeveritySchema>;

export const preflightCheckSchema = z.object({
  code: z.string(),
  title: z.string(),
  severity: preflightSeveritySchema,
  detail: z.string().optional(),
});
export type PreflightCheck = z.infer<typeof preflightCheckSchema>;

export const preflightApplicationResponseSchema = z.object({
  application_id: z.string(),
  protocol: protocolSchema,
  configuration_revision: z.number().int().positive(),
  checked_at: z.string().datetime(),
  duration_ms: z.number().int().nonnegative(),
  checks: z.array(preflightCheckSchema),
  // The map values are coerced through `z.record` because Go's
  // `map[string]int` JSON-marshals into a string-keyed object.
  summary_by_severity: z.record(preflightSeveritySchema, z.number().int().nonnegative()),
});
export type PreflightApplicationResponse = z.infer<typeof preflightApplicationResponseSchema>;

export const pilotVerificationSchema = z.object({
  found: z.boolean(),
  verified: z.boolean(),
  user_id: z.string().optional(),
  configuration_revision: z.number().int().positive().optional(),
  protocol: z.enum(["oidc", "saml"]).optional(),
  protocol_event_id: z.string().optional(),
  observed_at: z.string().datetime().optional(),
  recorded_at: z.string().datetime().optional(),
  authorization_path: z.enum(["direct", "group"]).optional(),
  group_id: z.string().optional(),
  current_revision: z.boolean(),
  current_effective_access: z.boolean(),
  current_direct_pilot: z.boolean(),
});
export type PilotVerification = z.infer<typeof pilotVerificationSchema>;

export const updateAccessPolicySchema = z
  .object({
    is_requestable: z.boolean().optional(),
    default_duration_minutes: z.number().int().min(15).max(43200).optional(),
    max_duration_minutes: z.number().int().min(15).max(43200).optional(),
    auto_approve_self: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (
      val.default_duration_minutes != null &&
      val.max_duration_minutes != null &&
      val.default_duration_minutes > val.max_duration_minutes
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["default_duration_minutes"],
        message: "Default cannot exceed maximum.",
      });
    }
  });
export type UpdateAccessPolicyInput = z.infer<typeof updateAccessPolicySchema>;

// ── OIDC discovery document ─────────────────────────────────────────────────
//
// The subset of the OpenID Connect Discovery 1.0 metadata document
// (`/.well-known/openid-configuration`) that the setup-instructions
// panel renders. The endpoint URLs in this document are the SINGLE
// SOURCE OF TRUTH for what the IdP actually serves — the backend
// derives them from its own `oauth.EndpointPaths` config and the
// per-tenant issuer. The admin UI MUST render these live values
// rather than re-deriving paths client-side; hardcoding the paths
// on the frontend is exactly the drift that produced wrong
// `/oauth2/*` URLs in the setup panel. Unknown keys are stripped by
// Zod's default object parsing — we only pin the fields we display.
export const oidcDiscoverySchema = z.object({
  issuer: z.string().url(),
  authorization_endpoint: z.string().url(),
  token_endpoint: z.string().url(),
  // Optional per OIDC Discovery §3, though our backend always emits
  // it. A provider without a userinfo endpoint simply omits the row.
  userinfo_endpoint: z.string().url().optional(),
  jwks_uri: z.string().url(),
  // RP-initiated logout (OIDC RP-Initiated Logout 1.0). Optional —
  // absent when the tenant has not enabled end-session.
  end_session_endpoint: z.string().url().optional(),
});
export type OidcDiscovery = z.infer<typeof oidcDiscoverySchema>;

// ── SAML IdP signing certificate ─────────────────────────────────────────
//
// Wire shape returned by:
//   GET  /api/v1/tenants/{tenant_id}/applications/{app_id}/saml/cert
//   POST /api/v1/tenants/{tenant_id}/applications/{app_id}/saml/cert/rotate
//
// Mirrors backend `CertResponse` in
// backend/internal/tenant/saml/saml.go.
export const samlCertStatusSchema = z.enum(["active", "rotated", "revoked"]);
export type SAMLCertStatus = z.infer<typeof samlCertStatusSchema>;

export const samlCertSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  application_id: z.string().uuid(),
  status: samlCertStatusSchema,
  created_at: z.string().datetime(),
  expires_at: z.string().datetime(),
  cert_pem: z.string(),
  fingerprint: z.string().optional(),
});
export type SAMLCert = z.infer<typeof samlCertSchema>;

export const samlRotateReasonSchema = z.enum([
  "scheduled",
  "compromise",
  "key_algo_upgrade",
  "manual",
]);
export type SAMLRotateReason = z.infer<typeof samlRotateReasonSchema>;

export const samlRotateRequestSchema = z.object({
  reason: samlRotateReasonSchema.optional(),
  justification: z.string().optional(),
});
export type SAMLRotateRequest = z.infer<typeof samlRotateRequestSchema>;

// THE CONTRACT'S OWN VALIDATOR, not a copy of it.
//
// This response was hand-written here and pinned runtime_effect to
// "legacy_unchanged", one of the four values the server sends. The moment a
// tenant turned Application sign-in on — when the area reports "enforced" —
// the parse threw, the capability read failed, and every application's
// configuration form rendered read-only, telling the administrator they had
// read-only access. The console was at its most locked exactly when the product
// was most on.
//
// A second copy of a wire shape drifts from the first in silence. This one is
// generated from api/openapi/admin.yaml, and `make docs-check` fails on drift.
export { workspaceDecisionSchema, applicationWorkspaceCapabilitiesSchema };
export type WorkspaceDecision = z.infer<typeof workspaceDecisionSchema>;

/**
 * The estate view: every live SAML application and what holds the value it
 * sends. Documented in api/openapi/admin.yaml; the enums are the server's
 * vocabulary, and an unknown value is refused rather than rendered raw, so a
 * newer server cannot make this table say something it has no words for.
 */
export type NameIDReport = z.infer<typeof nameIDReportSchema>;
